import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { serviceurl } from 'src/app/constant/constant';
import { CurrentUserService } from 'src/app/services/current-user.service';
import {
  AutoPostField,
  CommunityBan,
  CommunityInvite,
  CommunityInviteLink,
  CommunityJoinRequest,
  CommunityLinkPreview,
  CommunityLogEntry,
  CommunityMember,
  CommunityPrivacy,
  CommunityRole,
  MyCommunityInvite,
  PaginatedResponse,
  SocialCommunity,
  SocialAuthor,
  SocialComment,
  SocialFeedScope,
  SocialGameGroup,
  SocialGroup,
  SocialMessage,
  SocialNotification,
  SocialPost,
  SocialProfile,
  SocialSearchResults,
  SocialThread,
  ReportReason,
} from '../models/social-feed.model';

@Injectable({
  providedIn: 'root',
})
export class SocialFeedService {
  private baseUrl: string;
  /** Logged-in user's id, kept in sync with the app's saved currentUser. */
  private currentUserId: number | null = null;

  constructor(private http: HttpClient, currentUserService: CurrentUserService) {
    const rawUrl = serviceurl || '';
    this.baseUrl = rawUrl.endsWith('/') ? rawUrl : rawUrl + '/';
    // currentUser is a BehaviorSubject seeded from localStorage, so the id is
    // available synchronously — before any component makes its first call.
    currentUserService.currentUser.subscribe((user) => {
      this.currentUserId = user?.id ?? null;
    });
  }

  /**
   * Every social call carries the acting user's id as `user_id` (the same
   * convention the rest of the app's APIs use). On `by-user/<id>/...` calls
   * the URL's id is the *target* profile; `user_id` is always the *caller*.
   */
  private withUser(params: HttpParams = new HttpParams()): HttpParams {
    return this.currentUserId != null ? params.set('user_id', String(this.currentUserId)) : params;
  }

  private get<T>(path: string, params?: HttpParams): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}${path}`, { params: this.withUser(params) });
  }

  private post<T>(path: string, body: unknown = {}): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}${path}`, body, { params: this.withUser() });
  }

  private patch<T>(path: string, body: unknown = {}): Observable<T> {
    return this.http.patch<T>(`${this.baseUrl}${path}`, body, { params: this.withUser() });
  }

  private remove<T>(path: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}${path}`, { params: this.withUser() });
  }

  // ---------------------------------------------------------------------
  // Feed / posts
  // ---------------------------------------------------------------------

  getFeed(options: {
    scope?: SocialFeedScope;
    group?: number;
    gameGroup?: number;
    community?: number;
    map?: string;
    game?: number;
    author?: number;
    page?: number;
  } = {}): Observable<PaginatedResponse<SocialPost>> {
    let params = new HttpParams();
    if (options.scope) {
      params = params.set('scope', options.scope);
    }
    if (options.group != null) {
      params = params.set('group', String(options.group));
    }
    if (options.gameGroup != null) {
      params = params.set('game_group', String(options.gameGroup));
    }
    if (options.community != null) {
      params = params.set('community', String(options.community));
    }
    if (options.map) {
      params = params.set('map', options.map);
    }
    if (options.game != null) {
      params = params.set('game', String(options.game));
    }
    if (options.author != null) {
      params = params.set('author', String(options.author));
    }
    if (options.page) {
      params = params.set('page', String(options.page));
    }
    return this.get<PaginatedResponse<SocialPost>>('api/social/posts/', params);
  }

  /**
   * Post creation always goes through FormData — the backend post endpoint
   * only accepts multipart/form-urlencoded bodies (so image attachments and
   * plain text posts share one code path). A post targets at most one of
   * group (map group) or gameGroup (game group); omit both for a personal post.
   */
  createPost(payload: {
    content: string;
    group?: number;
    gameGroup?: number;
    community?: number;
    /** Up to 4 photos (sent as repeated `images` parts). */
    images?: File[];
    video?: File;
    activityLabel?: string;
    /** A poll: the post's text is the question. `hours` null = never ends. */
    poll?: { options: string[]; hours: number | null };
  }): Observable<SocialPost> {
    const formData = new FormData();
    formData.append('content', payload.content);
    if (payload.activityLabel) {
      formData.append('activity_label', payload.activityLabel);
    }
    if (payload.group != null) {
      formData.append('group', String(payload.group));
    }
    if (payload.gameGroup != null) {
      formData.append('game_group', String(payload.gameGroup));
    }
    if (payload.community != null) {
      formData.append('community', String(payload.community));
    }
    for (const image of payload.images || []) {
      formData.append('images', image);
    }
    if (payload.video) {
      formData.append('video', payload.video);
    }
    if (payload.poll) {
      payload.poll.options.forEach((option) => formData.append('poll_options', option));
      if (payload.poll.hours) {
        formData.append('poll_hours', String(payload.poll.hours));
      }
    }
    return this.post<SocialPost>('api/social/posts/', formData);
  }

  getPost(postId: number): Observable<SocialPost> {
    return this.get<SocialPost>(`api/social/posts/${postId}/`);
  }

  pinPost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/pin/`);
  }

  unpinPost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/unpin/`);
  }

  /** Cast or change your vote; returns the post with the updated poll. */
  votePost(postId: number, optionId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/vote/`, { option: optionId });
  }

  deletePost(postId: number): Observable<void> {
    return this.remove<void>(`api/social/posts/${postId}/`);
  }

  /**
   * Content-only — the backend deliberately ignores any other field on edit.
   * Sent as FormData, not JSON: the post endpoint's parser is scoped to
   * multipart/form bodies only (for file uploads), so a JSON PATCH 415s.
   */
  editPost(postId: number, content: string): Observable<SocialPost> {
    const formData = new FormData();
    formData.append('content', content);
    return this.patch<SocialPost>(`api/social/posts/${postId}/`, formData);
  }

  likePost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/like/`);
  }

  unlikePost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/unlike/`);
  }

  boostPost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/boost/`);
  }

  unboostPost(postId: number): Observable<SocialPost> {
    return this.post<SocialPost>(`api/social/posts/${postId}/unboost/`);
  }

  // ---------------------------------------------------------------------
  // Comments
  // ---------------------------------------------------------------------

  getComments(postId: number): Observable<SocialComment[]> {
    return this.get<SocialComment[]>(`api/social/posts/${postId}/comments/`);
  }

  addComment(postId: number, content: string, parentId?: number | null): Observable<SocialComment> {
    return this.post<SocialComment>(
      `api/social/posts/${postId}/comments/`,
      parentId ? { content, parent: parentId } : { content }
    );
  }

  editComment(commentId: number, content: string): Observable<SocialComment> {
    return this.patch<SocialComment>(`api/social/comments/${commentId}/`, { content });
  }

  likeComment(commentId: number): Observable<SocialComment> {
    return this.post<SocialComment>(`api/social/comments/${commentId}/like/`);
  }

  unlikeComment(commentId: number): Observable<SocialComment> {
    return this.post<SocialComment>(`api/social/comments/${commentId}/unlike/`);
  }

  deleteComment(commentId: number): Observable<void> {
    return this.remove<void>(`api/social/comments/${commentId}/`);
  }

  // ---------------------------------------------------------------------
  // Groups
  // ---------------------------------------------------------------------

  getGroups(): Observable<SocialGroup[]> {
    return this.get<any>('api/social/groups/').pipe(
      map((res) => (Array.isArray(res) ? res : res?.results || []))
    );
  }

  getGroup(groupId: number): Observable<SocialGroup> {
    return this.get<SocialGroup>(`api/social/groups/${groupId}/`);
  }

  getGroupMembers(groupId: number): Observable<SocialAuthor[]> {
    return this.get<SocialAuthor[]>(`api/social/groups/${groupId}/members/`);
  }

  // ---------------------------------------------------------------------
  // Game groups (a "game" is a distinct entity from a map — a map can host
  // multiple games, each with its own GM/roster; see backend social/access.py)
  // ---------------------------------------------------------------------

  getGameGroups(): Observable<SocialGameGroup[]> {
    return this.get<any>('api/social/game-groups/').pipe(
      map((res) => (Array.isArray(res) ? res : res?.results || []))
    );
  }

  getGameGroup(gameGroupId: number): Observable<SocialGameGroup> {
    return this.get<SocialGameGroup>(`api/social/game-groups/${gameGroupId}/`);
  }

  getGameGroupMembers(gameGroupId: number): Observable<SocialAuthor[]> {
    return this.get<SocialAuthor[]>(`api/social/game-groups/${gameGroupId}/members/`);
  }

  // ---------------------------------------------------------------------
  // Profile
  // ---------------------------------------------------------------------

  getMyProfile(): Observable<SocialProfile> {
    return this.get<SocialProfile>('api/social/profiles/me/');
  }

  updateMyHandle(handle: string): Observable<SocialProfile> {
    return this.patch<SocialProfile>('api/social/profiles/me/', { handle });
  }

  updateMyProfile(bio: string): Observable<SocialProfile> {
    return this.patch<SocialProfile>('api/social/profiles/me/', { bio });
  }

  updateMySettings(fields: Partial<Record<AutoPostField, boolean>>): Observable<SocialProfile> {
    return this.patch<SocialProfile>('api/social/profiles/me/', fields);
  }

  getProfileByUserId(userId: number): Observable<SocialProfile> {
    return this.get<SocialProfile>(`api/social/profiles/by-user/${userId}/`);
  }

  followUser(userId: number): Observable<SocialProfile> {
    return this.post<SocialProfile>(`api/social/profiles/by-user/${userId}/follow`);
  }

  unfollowUser(userId: number): Observable<SocialProfile> {
    return this.post<SocialProfile>(`api/social/profiles/by-user/${userId}/unfollow`);
  }

  /** Symmetric: also removes any existing follow between the two of you, either direction. */
  blockUser(userId: number): Observable<SocialProfile> {
    return this.post<SocialProfile>(`api/social/profiles/by-user/${userId}/block`);
  }

  unblockUser(userId: number): Observable<SocialProfile> {
    return this.post<SocialProfile>(`api/social/profiles/by-user/${userId}/unblock`);
  }

  getFollowers(userId: number): Observable<PaginatedResponse<SocialAuthor>> {
    return this.get<PaginatedResponse<SocialAuthor>>(`api/social/profiles/by-user/${userId}/followers/`);
  }

  getFollowing(userId: number): Observable<PaginatedResponse<SocialAuthor>> {
    return this.get<PaginatedResponse<SocialAuthor>>(`api/social/profiles/by-user/${userId}/following/`);
  }

  // ---------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------

  getNotifications(page?: number): Observable<PaginatedResponse<SocialNotification>> {
    return this.get<PaginatedResponse<SocialNotification>>(
      'api/social/notifications/',
      page ? new HttpParams().set('page', String(page)) : undefined
    );
  }

  /** Just the unread number — cheap enough to poll; the list is fetched only when it changes. */
  getUnreadNotificationCount(): Observable<number> {
    return this.get<{ count: number }>('api/social/notifications/unread_count/').pipe(map((res) => res.count));
  }

  /** Both header badges (notifications + messages) in one request — what the
   * feed page's 30s poll actually uses; the two counts above still exist for
   * anywhere that only needs one of them. */
  getActivityUnreadCounts(): Observable<{ notifications: number; messages: number }> {
    return this.get<{ notifications: number; messages: number }>('api/social/activity/unread_count/');
  }

  markNotificationRead(notificationId: number): Observable<SocialNotification> {
    return this.patch<SocialNotification>(`api/social/notifications/${notificationId}/read/`);
  }

  markAllNotificationsRead(): Observable<void> {
    return this.patch<void>('api/social/notifications/read_all/');
  }

  // ---------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------

  search(query: string): Observable<SocialSearchResults> {
    return this.get<SocialSearchResults>('api/social/search/', new HttpParams().set('q', query));
  }

  // ---------------------------------------------------------------------
  // Reports
  // ---------------------------------------------------------------------

  /** Exactly one of postId/commentId. No in-app review yet - this just confirms receipt. */
  report(target: { postId: number } | { commentId: number }, reason: ReportReason, details?: string): Observable<void> {
    const body: Record<string, unknown> = { reason, details: details || '' };
    if ('postId' in target) {
      body['post'] = target.postId;
    } else {
      body['comment'] = target.commentId;
    }
    return this.post<void>('api/social/reports/', body);
  }

  // ---------------------------------------------------------------------
  // Direct messages
  // ---------------------------------------------------------------------

  /** The inbox: one row per person you've messaged, newest conversation first. */
  getMessageThreads(page?: number): Observable<PaginatedResponse<SocialThread>> {
    return this.get<PaginatedResponse<SocialThread>>(
      'api/social/messages/',
      page ? new HttpParams().set('page', String(page)) : undefined
    );
  }

  /** Cheap poll target for the header's message badge. */
  getUnreadMessageCount(): Observable<number> {
    return this.get<{ count: number }>('api/social/messages/unread_count/').pipe(map((res) => res.count));
  }

  /** A page of messages with one person, newest first (mirrors notifications' paging). */
  getMessages(userId: number, page?: number): Observable<PaginatedResponse<SocialMessage>> {
    return this.get<PaginatedResponse<SocialMessage>>(
      `api/social/messages/with/${userId}/`,
      page ? new HttpParams().set('page', String(page)) : undefined
    );
  }

  sendMessage(userId: number, content: string): Observable<SocialMessage> {
    return this.post<SocialMessage>(`api/social/messages/with/${userId}/`, { content });
  }

  markThreadRead(userId: number): Observable<void> {
    return this.patch<void>(`api/social/messages/with/${userId}/read/`);
  }

  // ---------------------------------------------------------------------
  // Communities
  // ---------------------------------------------------------------------

  getCommunities(options: { scope?: 'all' | 'mine'; q?: string; privacy?: CommunityPrivacy; page?: number } = {}):
    Observable<PaginatedResponse<SocialCommunity>> {
    let params = new HttpParams();
    if (options.scope) params = params.set('scope', options.scope);
    if (options.q) params = params.set('q', options.q);
    if (options.privacy) params = params.set('privacy', options.privacy);
    if (options.page) params = params.set('page', String(options.page));
    return this.get<PaginatedResponse<SocialCommunity>>('api/social/communities/', params);
  }

  getCommunity(slug: string): Observable<SocialCommunity> {
    return this.get<SocialCommunity>(`api/social/communities/${slug}/`);
  }

  /** Create (no slug) or edit. Sent as FormData so a picture can go along. */
  saveCommunity(
    fields: { name?: string; description?: string; rules?: string; privacy?: CommunityPrivacy; members_can_invite?: boolean },
    image?: File | null,
    slug?: string,
    removeImage = false
  ): Observable<SocialCommunity> {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) form.append(key, String(value));
    });
    if (image) form.append('image', image);
    if (removeImage) form.append('remove_image', 'true');
    return slug
      ? this.patch<SocialCommunity>(`api/social/communities/${slug}/`, form)
      : this.post<SocialCommunity>('api/social/communities/', form);
  }

  deleteCommunity(slug: string): Observable<void> {
    return this.remove<void>(`api/social/communities/${slug}/`);
  }

  joinCommunity(slug: string): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/communities/${slug}/join/`);
  }

  leaveCommunity(slug: string): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/communities/${slug}/leave/`);
  }

  requestToJoin(slug: string, message: string): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/communities/${slug}/request/`, { message });
  }

  cancelJoinRequest(slug: string): Observable<SocialCommunity> {
    return this.remove<SocialCommunity>(`api/social/communities/${slug}/request/`);
  }

  getJoinRequests(slug: string, page?: number): Observable<PaginatedResponse<CommunityJoinRequest>> {
    return this.get(`api/social/communities/${slug}/requests/`, page ? new HttpParams().set('page', String(page)) : undefined);
  }

  decideJoinRequest(slug: string, requestId: number, approve: boolean): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/requests/${requestId}/${approve ? 'approve' : 'decline'}/`);
  }

  getCommunityMembers(slug: string, options: { q?: string; role?: CommunityRole; page?: number } = {}):
    Observable<PaginatedResponse<CommunityMember>> {
    let params = new HttpParams();
    if (options.q) params = params.set('q', options.q);
    if (options.role) params = params.set('role', options.role);
    if (options.page) params = params.set('page', String(options.page));
    return this.get(`api/social/communities/${slug}/members/`, params);
  }

  setCommunityRole(slug: string, userId: number, role: 'moderator' | 'member'): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/members/${userId}/role/`, { role });
  }

  removeCommunityMember(slug: string, userId: number, ban: boolean, reason = ''): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/members/${userId}/remove/`, { ban, reason });
  }

  transferCommunity(slug: string, userId: number): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/communities/${slug}/transfer/`, { user_id: userId });
  }

  getCommunityBans(slug: string, page?: number): Observable<PaginatedResponse<CommunityBan>> {
    return this.get(`api/social/communities/${slug}/bans/`, page ? new HttpParams().set('page', String(page)) : undefined);
  }

  unbanFromCommunity(slug: string, userId: number): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/bans/${userId}/unban/`);
  }

  getCommunityLog(slug: string, page?: number): Observable<PaginatedResponse<CommunityLogEntry>> {
    return this.get(`api/social/communities/${slug}/log/`, page ? new HttpParams().set('page', String(page)) : undefined);
  }

  getCommunityInvites(slug: string, page?: number): Observable<PaginatedResponse<CommunityInvite>> {
    return this.get(`api/social/communities/${slug}/invites/`, page ? new HttpParams().set('page', String(page)) : undefined);
  }

  inviteToCommunity(slug: string, userId: number): Observable<CommunityInvite> {
    return this.post<CommunityInvite>(`api/social/communities/${slug}/invites/`, { user_id: userId });
  }

  revokeCommunityInvite(slug: string, inviteId: number): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/invites/${inviteId}/revoke/`);
  }

  getMyCommunityInvites(page?: number): Observable<PaginatedResponse<MyCommunityInvite>> {
    return this.get('api/social/community-invites/', page ? new HttpParams().set('page', String(page)) : undefined);
  }

  answerCommunityInvite(inviteId: number, accept: boolean): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/community-invites/${inviteId}/${accept ? 'accept' : 'decline'}/`);
  }

  getCommunityLinks(slug: string): Observable<PaginatedResponse<CommunityInviteLink>> {
    return this.get(`api/social/communities/${slug}/links/`);
  }

  createCommunityLink(slug: string, days: number | null, maxUses: number | null): Observable<CommunityInviteLink> {
    return this.post<CommunityInviteLink>(`api/social/communities/${slug}/links/`, { days, max_uses: maxUses });
  }

  revokeCommunityLink(slug: string, linkId: number): Observable<void> {
    return this.post<void>(`api/social/communities/${slug}/links/${linkId}/revoke/`);
  }

  previewCommunityLink(token: string): Observable<CommunityLinkPreview> {
    return this.get<CommunityLinkPreview>(`api/social/community-links/${token}/`);
  }

  joinCommunityByLink(token: string): Observable<SocialCommunity> {
    return this.post<SocialCommunity>(`api/social/community-links/${token}/`);
  }

  // ---------------------------------------------------------------------
  // Media helper
  // ---------------------------------------------------------------------

  resolveImageUrl(path?: string | null): string {
    if (!path) {
      return '';
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return this.baseUrl.replace(/\/$/, '') + path;
  }
}
